import { type ChangeEvent, type ReactNode, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MAX_RADIUS, MIN_RADIUS } from '../utils/constants.ts';
import { countDecimals } from '../utils/countDecimals.ts';

export const useRadius = () => {
  const { t } = useTranslation();
  const [radius, setRadius] = useState('');
  const [radiusError, setRadiusError] = useState<null | string | ReactNode>(null);

  const clearErrors = useCallback(() => {
    setRadiusError(null);
  }, []);

  const validateRadius = useCallback(
    (value: string): boolean =>
      !Number.isNaN(Number(value)) &&
      Number(value) >= MIN_RADIUS &&
      Number(value) <= MAX_RADIUS &&
      countDecimals(Number(value)) <= 3,
    []
  );

  const onRadiusChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      const { value } = event.target;
      setRadius(value);

      const isRadiusError = !validateRadius(value);

      setRadiusError(
        isRadiusError ? t('Allowed range: {MIN_RADIUS}–{MAX_RADIUS} (3 decimals)', { MIN_RADIUS, MAX_RADIUS }) : null
      );
    },
    [t, validateRadius]
  );

  return {
    radius,
    setRadius,
    onRadiusChange,
    radiusError,
    setRadiusError,
    clearErrors,
    validateRadius,
  };
};
